import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { BehaviorSubject, Subject } from 'rxjs';
import { IVideoROIFrame } from '../../directives';

export interface IVideoSource {
  url: string;
  id: string;
  fileName?: string;
  thumbnail?: string;
}

@Component({
    selector: 'ts-video-viewer',
    templateUrl: './video-viewer.component.html',
    styleUrls: ['./video-viewer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'videoViewer',
    standalone: false
})
export class VideoViewerComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() sources: IVideoSource[];
  @Input() videoBlobUrl?: string;
  @Input() isIOS: boolean;
  @Input() isMobile: boolean;
  @Input() public readonly selectedIndex: number;
  @Output() resizeEvent = new EventEmitter<void>();
  @Output() assetChanged = new EventEmitter<string>();
  @Output() unselectRois = new EventEmitter<void>();
  @Output() delete = new EventEmitter<void>();

  private readonly DRAG_THRESHOLD = 20;

  dragging = false;
  lastMouseX: number;
  lastMouseY: number;
  zoomLevel = 1;
  translateX = 0;
  translateY = 0;
  private dragDistance = 0;
  isLoading = true;

  @ViewChild('video', { static: true }) video: ElementRef<HTMLVideoElement>;
  @ViewChild('tracker') tracker: any;
  @ViewChild('canvas', { static: true }) canvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('bigCanvasContainer', { static: true }) canvasContainer: any;
  @ViewChild('resizeHandle', { static: true }) resizeHandle: ElementRef;
  @ViewChild('videoBar', { static: true }) videoBar: ElementRef;
  @ViewChild('trackbar', { read: ElementRef }) trackBar: ElementRef<any>;

  private _useCanvas: boolean;

  private _canvasTransform: number[] = [1, 0, 0, 1, 0, 0];
  get canvasTransform() {
    return [...this._canvasTransform];
  }
  private _canvasTransformB$ = new BehaviorSubject<number[]>(this._canvasTransform);
  private _showVideoSelectionOverlay = new BehaviorSubject<boolean>(false);
  slowSeek: Subject<boolean> = new Subject<boolean>();

  get canvasTransform$() {
    return this._canvasTransformB$.asObservable();
  }

  get playListCount() {
    return this.sources?.length;
  }
  get useCanvas() {
    return this._useCanvas;
  }

  get showVideoSelectionOverlay$() {
    return this._showVideoSelectionOverlay.asObservable();
  }

  frameDrawn$ = new EventEmitter<void>();
  ratio: number;
  videoDuration = 0;

  ctx: CanvasRenderingContext2D;
  playbackSpeed = 1;
  speedDropdownOpen = false;
  videoCurrentTime = 0;
  zoom = 1;
  private _videoToCanvasContainerFactor: number;

  private startY!: number;
  private startHeight!: number;

  constructor(@Inject(DOCUMENT) private _document: Document, private cdr: ChangeDetectorRef) {}

  /** Live width of the timeline (px), tracked so the number of minute markers
   *  adapts to the available space instead of always drawing a fixed 25. */
  private timelineWidth = 0;
  private timelineResizeObserver?: ResizeObserver;
  /** Minimum horizontal room each marker label needs before they crowd. */
  private readonly MIN_PX_PER_MARKER = 46;

  @HostListener('document:keydown.space', ['$event'])
  public onSpacePressed(e) {
    if (e.target['nodeName'] !== 'BUTTON') {
      this.playPause();
    }
  }

  @HostListener('document:keydown.delete')
  @HostListener('document:keydown.backspace')
  @HostListener('document:keydown.control.delete')
  @HostListener('document:keydown.meta.backspace')
  public onDelPressed() {
    this.delete.emit();
  }

  @HostListener('document:keydown', ['$event'])
  handleKey(event: KeyboardEvent) {
    if (event.code === 'ArrowLeft') {
      if (event.ctrlKey || event.metaKey) {
        this._seekBy(-0.03);
      } else {
        this._seekBy(-1);
      }
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.resizeCanvas();
  }

  async ngOnInit() {
    this._useCanvas = !this.isIOS;
    this._setupHandlers();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['videoBlobUrl'] && this.videoBlobUrl) {
      this.setSource(this.videoBlobUrl);
    }
  }

  ngAfterViewInit(): void {
    // this.canvas = this._document.getElementById('bigCanvas') as HTMLCanvasElement;
    // this.resizeCanvas();
    this.ctx = this.canvas.nativeElement.getContext('2d');
    this.initializeResizing();
    this.observeTimelineWidth();
  }

  /** Track the timeline width so `getTimelineMarkers` can thin the labels out as
   *  space shrinks (window resize, protocol-panel collapse, layout changes). */
  private observeTimelineWidth(): void {
    const el = this.trackBar?.nativeElement as HTMLElement | undefined;
    if (!el) return;
    this.timelineWidth = el.clientWidth;
    if (typeof ResizeObserver !== 'undefined') {
      this.timelineResizeObserver = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect?.width ?? el.clientWidth;
        if (width && Math.round(width) !== Math.round(this.timelineWidth)) {
          this.timelineWidth = width;
          this.cdr.markForCheck();
        }
      });
      this.timelineResizeObserver.observe(el);
    }
  }

  ngOnDestroy(): void {
    this.canvas.nativeElement.removeEventListener('mousedown', this.activatePan);
    this.canvas.nativeElement.removeEventListener('mousemove', this.pan);
    this._document.removeEventListener('mouseup', this.stopPan);
    this.timelineResizeObserver?.disconnect();
  }

  setPlaybackSpeed(speed: number) {
    this.playbackSpeed = speed;
    this.video.nativeElement.playbackRate = speed;
    this.toggleSpeedDropdown();
  }

  toggleSpeedDropdown() {
    this.speedDropdownOpen = !this.speedDropdownOpen;
  }

  getTimelineMarkers(videoDuration) {
    if (videoDuration === 0) return [];
    const roundedDuration = Math.round(videoDuration);
    const DEFAULT_N_MARKERS = 25;
    // Cap the marker count by the available width so labels don't crowd on
    // narrow screens; fall back to the default until the width is measured.
    const widthCap =
      this.timelineWidth > 0
        ? Math.max(1, Math.floor(this.timelineWidth / this.MIN_PX_PER_MARKER))
        : DEFAULT_N_MARKERS;
    const numMarkers = Math.max(1, Math.min(DEFAULT_N_MARKERS, roundedDuration, widthCap));
    const markerInterval = videoDuration / numMarkers;

    const timelineMarkers = [];

    for (let i = 0; i <= numMarkers; i++) {
      const time = i * markerInterval;
      timelineMarkers.push({
        time: this.formatTime(time),
        position: (time / videoDuration) * 100,
      });
    }

    return timelineMarkers;
  }

  formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs < 10 ? '0' + secs : secs}`;
  }

  relativeTime(time: number = this.videoCurrentTime) {
    return (time / this.video.nativeElement.duration) * 100;
  }

  selectVideo(index: number) {
    index = Math.max(0, Math.min(this.playListCount, index));
    if (index !== this.selectedIndex) {
      this.isLoading = true;
      this.assetChanged.emit(this.sources[index].id);
    }
  }

  setSource(blobUrl: string) {
    this._document.getElementById('videoSource')?.setAttribute('src', blobUrl);
    (this.video.nativeElement as HTMLVideoElement).load();
  }

  stop() {
    this.video.nativeElement.pause();
    this.video.nativeElement.currentTime = 0;
  }

  playPause() {
    this.video.nativeElement.paused ? this.video.nativeElement.play() : this.video.nativeElement.pause();
  }

  toggleLoop() {
    this.video.nativeElement.loop = !this.video.nativeElement.loop;
  }

  onMouseDown(event: MouseEvent) {
    this.startY = event.clientY;
    this.startHeight = this.videoBar.nativeElement.offsetHeight;
    event.preventDefault();
  }

  onMouseMove(event: MouseEvent) {
    const MAX_HEIGHT = 300;
    if (this.startY !== undefined && this.startHeight !== undefined) {
      const draggedHeight = this.startHeight + (this.startY - event.clientY);
      const newHeight = draggedHeight < MAX_HEIGHT ? draggedHeight : MAX_HEIGHT;
      this.resizeEvent.emit();

      if (newHeight > 50) {
        this.videoBar.nativeElement.style.height = `${newHeight}px`;
        this.resizeHandle.nativeElement.style.bottom = `${newHeight}px`;
      }
    }
  }

  onMouseUp() {
    this.startY = undefined;
    this.startHeight = undefined;
  }

  initializeResizing() {
    const resizeHandle = this.resizeHandle.nativeElement;
    resizeHandle.addEventListener('mousedown', this.onMouseDown.bind(this));
    document.addEventListener('mousemove', this.onMouseMove.bind(this));
    document.addEventListener('mouseup', this.onMouseUp.bind(this));
  }

  private _setupHandlers() {
    // if (!this.trackBar) this.trackBar = this._document.getElementsByClassName('videoProgressLine')[0] as HTMLElement;
    // (this.tracker).addEventListener('drag',this.seek,false)

    if (!this._useCanvas) {
      this.video.nativeElement.addEventListener('wheel', (event) => {
        if (event.ctrlKey || event.metaKey) {
          event.stopPropagation();
          this.zoom += Math.sign(event.deltaY) * 0.05;
        } else {
          this._seekBy(event);
        }
      });
    }

    if (this._useCanvas) {
      this.canvas.nativeElement.addEventListener(
        'wheel',
        (event) => {
          // event.preventDefault();
          if (event.ctrlKey || event.metaKey) {
            // event.preventDefault();
            const mousePosition = { x: event.clientX, y: event.clientY };
            this._zoomIn(-Math.sign(event.deltaY) * 0.05, mousePosition);
          } else {
            this._seekBy(event);
          }
        },
        { passive: true }
      );
      this.canvas.nativeElement.addEventListener('click', this.click);
      this.canvas.nativeElement.addEventListener('mousedown', this.activatePan);
      this.canvas.nativeElement.addEventListener('mousemove', this.pan);
      this.videoBar.nativeElement.addEventListener('click', () => this.unselectRois.emit());
      this._document.addEventListener('mouseup', this.stopPan.bind(this));
      this.prepareCanvasPlayback();
    }

    this._document.addEventListener('keyup', (e) => {
      switch (e.code) {
        case 'NumpadAdd':
          this._zoomIn(0.05);
          break;
        case 'NumpadSubtract':
          this._zoomIn(-0.05);
          break;
      }
    });

    this.video.nativeElement.addEventListener('loadedmetadata', (e) => {
      console.log('Video metadata: ', e);
      this.videoDuration = this.video.nativeElement.duration;
      this.resizeCanvas();
    });
  }

  private _zoomIn(delta: number, mousePosition?: { x: number; y: number }) {
    if (!mousePosition) return;

    // Current canvas transform values
    const [a, b, c, d, e, f] = this._canvasTransform;

    const newZoom = Math.max(0.1, this.zoomLevel + delta);
    const zoomFactor = newZoom / this.zoomLevel;
    this.zoomLevel = newZoom;

    // Calculate new translation
    const canvasRect = this.canvas.nativeElement.getBoundingClientRect();
    const mouseX = mousePosition.x - canvasRect.left;
    const mouseY = mousePosition.y - canvasRect.top;

    // Adjust translate values
    const translateX = e - (mouseX - e) * (zoomFactor - 1);
    const translateY = f - (mouseY - f) * (zoomFactor - 1);

    // Update canvas transform matrix
    this._canvasTransform[0] = a * zoomFactor; // scaleX
    this._canvasTransform[1] = b * zoomFactor; // skewX
    this._canvasTransform[2] = c; // skewY
    this._canvasTransform[3] = d * zoomFactor; // scaleY
    this._canvasTransform[4] = translateX; // translateX
    this._canvasTransform[5] = translateY; // translateY

    this._canvasTransformB$.next([...this._canvasTransform]);
  }

  private _translate(deltaX: number, deltaY: number) {
    this._canvasTransform[4] += deltaX;
    this._canvasTransform[5] += deltaY;
    this._canvasTransformB$.next([...this._canvasTransform]);
  }

  activatePan = (event: MouseEvent) => {
    if (!event.shiftKey) {
      this.dragging = true;
      this.lastMouseX = event.clientX;
      this.lastMouseY = event.clientY;
      this.dragDistance = 0;
    }
  };

  pan = (event: MouseEvent) => {
    if (this.dragging) {
      const deltaX = (event.clientX - this.lastMouseX) * 0.5;
      const deltaY = (event.clientY - this.lastMouseY) * 0.5;

      this.dragDistance += Math.abs(deltaX) + Math.abs(deltaY);

      if (this.dragDistance >= this.DRAG_THRESHOLD) {
        this._translate(deltaX, deltaY);
      }

      this.lastMouseX = event.clientX;
      this.lastMouseY = event.clientY;
    }
  };

  stopPan(event: MouseEvent) {
    this.dragging = false;
    this.lastMouseX = undefined;
    this.lastMouseY = undefined;
    event.preventDefault();
  }

  click = (event) => {
    if (this.dragDistance < this.DRAG_THRESHOLD) this.playPause();
    this.unselectRois.emit();
  };

  prepareCanvasPlayback() {
    this.canvasContainer = this._document.getElementById('bigCanvasContainer') as HTMLElement;
    // this.canvas = this._document.getElementById('bigCanvas') as HTMLCanvasElement;
    this.ctx = this.canvas.nativeElement.getContext('2d');
    const video = this.video.nativeElement;
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise
        .then((_) => {
          console.log(`Automatic playback started!`);
        })
        .catch((error) => {
          console.log(`Auto-play was prevented. Showing paused UI.`);
        });
    }
    setInterval(this.renderVideo.bind(this), 33);
  }

  resizeCanvas() {
    if (!this.video.nativeElement || !this.canvasContainer) return;

    this._videoToCanvasContainerFactor = this.fitRectangle(
      this.video.nativeElement.videoWidth,
      this.video.nativeElement.videoHeight,
      this.canvasContainer.clientWidth,
      this.canvasContainer.clientHeight
    );
    // Resize canvas
    this.canvas.nativeElement.width = this.video.nativeElement.videoWidth * this._videoToCanvasContainerFactor;
    this.canvas.nativeElement.height = this.video.nativeElement.videoHeight * this._videoToCanvasContainerFactor;
    this.canvas.nativeElement.style.width =
      this.video.nativeElement.videoWidth * this._videoToCanvasContainerFactor + 'px';
  }

  renderVideo() {
    this.ctx.fillStyle = 'black';

    this.ctx.save();
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.clearRect(0, 0, this.canvas.nativeElement.width, this.canvas.nativeElement.height);
    this.ctx.setTransform(
      this._canvasTransform[0],
      this._canvasTransform[1],
      this._canvasTransform[2],
      this._canvasTransform[3],
      this._canvasTransform[4],
      this._canvasTransform[5]
    );

    // Calculate letterboxing
    // TODO: REVIEW
    const canvasAR = this.canvasContainer.clientHeight / this.canvasContainer.clientWidth;
    const videoAR = this.video.nativeElement.videoHeight / this.video.nativeElement.videoWidth;
    const cAR = this.canvas.nativeElement.height / this.canvas.nativeElement.width;

    const letterBoxOffsets = {
      x: videoAR > canvasAR ? this.canvasContainer.clientWidth - this.canvasContainer.clientHeight / canvasAR / cAR : 0,
      y:
        videoAR < canvasAR
          ? this.canvasContainer.clientHeight - (this.canvasContainer.clientWidth / cAR) * canvasAR
          : 0,
    };

    letterBoxOffsets.x = 0;
    // letterBoxOffsets.y = 0;

    this.ctx.drawImage(
      this.video.nativeElement,
      this.video.nativeElement.videoWidth * ((1 - 1 / this.zoom) / 2),
      this.video.nativeElement.videoHeight * ((1 - 1 / this.zoom) / 2),
      this.video.nativeElement.videoWidth * (1 / this.zoom),
      this.video.nativeElement.videoHeight * (1 / this.zoom),
      letterBoxOffsets.x / 2,
      this.isMobile ? 0 : letterBoxOffsets.y / 2, // REVIEW: ¯\_(ツ)_/¯
      this.canvas.nativeElement.width - letterBoxOffsets.x,
      this.canvas.nativeElement.height - (this.isMobile ? letterBoxOffsets.y / 2 : letterBoxOffsets.y)
    );
    this.frameDrawn$.next();
    this.ctx.restore();
  }

  fitRectangle(win: number, hin: number, wout: number, hout: number): number {
    this.ratio = hin / win;
    const factor = Math.min(wout / win, hout / hin);
    return factor;
  }

  progressBarClicked(event) {
    if (event.target.id === 'tracker') return;
    const time =
      event.offsetX !== undefined
        ? (event.offsetX / this.trackBar.nativeElement.clientWidth) * this.video.nativeElement.duration
        : this.video.nativeElement.currentTime +
          (event.delta.x / this.trackBar.nativeElement.clientWidth) * this.video.nativeElement.duration;
    this._seek(time);
  }

  private _seek(time: number) {
    if (time > 0 && time < this.video.nativeElement.duration) {
      this.video.nativeElement.currentTime = time;
    }
  }

  private _seekBy(seconds: number | any) {
    switch (typeof seconds) {
      case 'number':
        this.video.nativeElement.currentTime += seconds;
        break;
      case 'object':
        this.video.nativeElement.currentTime -= Math.sign(seconds.deltaY) * (seconds.shiftKey ? 0.03 : 0.5);
        break;
    }
  }

  updateProgress(event) {
    this.videoCurrentTime = this.video.nativeElement.currentTime;
    this.tracker.nativeElement.style.left = `${(100 * this.videoCurrentTime) / this.video.nativeElement.duration}%`;
  }

  gotoBookmark(event, bookmark: IVideoROIFrame) {
    event?.stopPropagation();
    this.video.nativeElement.currentTime = bookmark.t1 || 0;
    this.video.nativeElement.pause();
  }

  toggleVideoSelectionOverlay(show = !this._showVideoSelectionOverlay.value) {
    this._showVideoSelectionOverlay.next(show);
  }

  onMetadataLoaded() {
    this.isLoading = false;
  }

  onLoadStart() {
    this.isLoading = true;
  }

  onCanPlayThrough() {
    this.isLoading = false;
    this.videoBar.nativeElement.hidden = false;
  }
}
